if (!customElements.get('scroll-reveal-text')) {
  customElements.define(
    'scroll-reveal-text',
    /**
     * ScrollRevealText - A scroll-based text reveal animation component
     *
     * This custom element creates a character-by-character text reveal effect as the user scrolls.
     * Text is split into individual character spans with dynamic opacity values based on scroll progress.
     *
     * - Operating modes:
     *   - sectionText: For dedicated text sections with pinned/sticky scrolling behavior
     *     - Pins the text element while scrolling
     *     - Calculates progress based on section scroll metrics
     *     - Supports multiple sequenced reveal elements in the same section
     *
     *   - sectionStickyImageCards: For inline text within sticky-image-cards sections
     *     - Uses viewport middle as the animation anchor point
     *     - Adapts to text height (different behavior for tall vs short text)
     *     - Falls back to standard offset-based calculation for other inline contexts
     *
     * - Animation decision logic:
     *   - Respects prefers-reduced-motion (disables animation entirely if set)
     *   - Mode is determined by parent section data-section-type attribute
     *   - Progress (0-1) is calculated from scroll position vs element position
     *   - Characters are revealed with graduated opacity: fully visible (1) → tail fade (0.82, 0.64, 0.46, 0.28) → base opacity (0.1)
     *
     * - Performance optimizations:
     *   - Uses requestAnimationFrame to throttle scroll/resize updates
     *   - Batches DOM updates to minimize reflows
     *   - Caches getBoundingClientRect results (100ms duration)
     *   - Only updates opacity when values change
     *
     * - Integration:
     *   - Works with text-highlight custom elements (activates them when text is revealed)
     *   - Respects theme header height for viewport calculations
     *   - Cleans up DOM changes on disconnection (reverts to original HTML)
     */
    class ScrollRevealText extends HTMLElement {
      constructor() {
        super();

        // Animation opacity constants
        this.BASE_OPACITY = '0.1'; // Minimum opacity for unrevealed characters
        this.TAIL_OPACITIES = ['0.82', '0.64', '0.46', '0.28']; // Graduated fade for 4 characters following the reveal line

        // Scroll behavior constants
        this.DURATION_MULTIPLIER = 2.5; // Larger = slower reveal (needs more scroll distance)
        this.START_OFFSET_RATIO = 0.8; // Start animation when element is 80% down viewport (inline mode)
        this.END_OFFSET_RATIO = 0.4; // End animation when element is 40% down viewport (inline mode)
        this.MOBILE_BREAKPOINT = 768; // Viewport width threshold for mobile detection

        // Event handlers (bound methods to preserve context)
        this.onScrollCallback = () => this.onScroll();
        this.onResizeCallback = () => this.onResize();
        this.onVisibilityChangeCallback = () => this.onVisibilityChange();

        // Animation state
        this.rafId = 0; // Current requestAnimationFrame ID (0 = no pending animation)
        this._listening = false; // Whether event listeners are currently attached
        this.reverts = []; // Array of functions to restore original HTML on cleanup
        this.allChars = []; // Array of all character span elements created by splitToChars()
        this.state = {lastVisible: -1}; // Tracks last visible character count to avoid redundant updates

        // Performance optimization: cached getBoundingClientRect
        this.cachedRect = null; // Cached DOMRect object
        this.cachedRectTimestamp = 0; // Timestamp when rect was cached
        this.RECT_CACHE_DURATION = 100; // Cache duration in milliseconds

        // Sequence state (for multiple scroll-reveal-text elements in same section)
        this.sequenceIndex = 0; // This element's position in sequence (0-based)
        this.sequenceTotal = 1; // Total number of elements in sequence
        this.sequenceDataStale = true; // Flag to indicate when sequence data needs recalculation
        this._lastPeerCount = 0; // Tracks number of peer elements to detect when they're added/removed
        this._checkPeerCountUntil = 10; // Check for peer changes for first N scroll events to handle init race conditions

        // Accessibility: check for reduced motion preference
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      /**
       * Main setup method:
       * - Respects prefers-reduced-motion accessibility setting
       * - Detects operating mode (sectionText vs sectionStickyImageCards)
       * - Splits text into character spans and initializes animation state
       * - Sets up scroll/resize event listeners
       */
      connectedCallback() {
        // Skip initialization if reduced motion is preferred
        if (this.prefersReducedMotion) {
          return;
        }

        const textSection = this.closest('[data-section-type="text"]');
        const stickyCardsSection = this.closest('[data-section-type="sticky-image-cards"]');

        this.mode = textSection && !stickyCardsSection ? 'sectionText' : 'sectionStickyImageCards';

        this.DURATION_MULTIPLIER = this.mode === 'sectionStickyImageCards' ? 1.5 : this.DURATION_MULTIPLIER;
        this.reverts = [];
        this.allChars = [];
        this.state = {lastVisible: -1};
        this.cachedRect = null;
        this.cachedRectTimestamp = 0;

        if (this.mode === 'sectionText') {
          this.section = textSection;
          // Only process this element's own content so multiple blocks stay independent
          this.contentRoot = this;

          try {
            const {chars, revert} = this.splitToChars(this.contentRoot);
            this.allChars.push(...chars);
            this.reverts.push(revert);
          } catch (error) {
            console.warn('scroll-reveal-text: Error splitting text', error);
            return;
          }

          if (this.allChars.length === 0) return;
          // Ensure highlights start inactive until their text is fully revealed.
          this.updateHighlightStates();

          this.wrapper = this.section.querySelector(':scope > div');
          this.textEl = this.section.querySelector('.text');
          this.setSequenceData();

          if (!this.wrapper || !this.textEl) {
            console.warn('scroll-reveal-text: Required elements not found');
            return;
          }

          // Calculate dynamic DURATION_MULTIPLIER based on text height
          // Shorter text (like single-line headings) animates faster
          // Taller text (like multi-paragraph body) animates slower
          this.calculateDurationMultiplier();

          this.metrics = {
            start: 0,
            duration: Math.max(1, Math.round((window.innerHeight || 0) * this.DURATION_MULTIPLIER)),
          };

          this.applyPinnedSizing();
          this.updateMetrics();
          this.updateSection();
        } else {
          try {
            const {chars, revert} = this.splitToChars(this);
            this.allChars.push(...chars);
            this.reverts.push(revert);
          } catch (error) {
            console.warn('scroll-reveal-text: Error splitting text', error);
            return;
          }

          if (this.allChars.length === 0) return;
          this.updateHighlightStates();
          this.updateInline();
        }

        // Add event listeners only if not already listening
        if (!this._listening) {
          document.addEventListener('theme:scroll', this.onScrollCallback, {passive: true});
          document.addEventListener('theme:resize', this.onResizeCallback, {passive: true});
          // Safari fix: Listen for visibility changes (e.g., when navigating back to page)
          document.addEventListener('visibilitychange', this.onVisibilityChangeCallback, {passive: true});
          this._listening = true;
        }

        // Safari fix: Trigger initial update after layout is complete
        // Wait for fonts and CSS to load before calculating metrics
        const performInitialUpdate = () => {
          if (!this.isConnected) return;

          try {
            if (this.mode === 'sectionText') {
              // Recalculate metrics to ensure correct values after layout
              this.applyPinnedSizing();
              this.updateMetrics();
              this.updateSection();
            } else {
              this.updateInline();
            }
          } catch (error) {
            console.error('scroll-reveal-text: Error in initial update', error);
          }
        };

        // Schedule update using double RAF for Safari layout completion
        const scheduleUpdate = () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(performInitialUpdate);
          });
        };

        // Wait for document ready state and fonts
        if (document.readyState === 'complete') {
          // Document already loaded - update immediately
          scheduleUpdate();
        } else {
          // Wait for load event (includes CSS, fonts, images)
          window.addEventListener('load', scheduleUpdate, {once: true});
        }
      }

      /**
       * Throttled scroll event handler using requestAnimationFrame
       * Delegates to appropriate update method based on operating mode
       */
      onScroll() {
        if (!this.isConnected || this.allChars.length === 0) return;
        if (this.rafId) return;

        this.rafId = requestAnimationFrame(() => {
          this.rafId = 0;

          if (!this.isConnected) return;

          try {
            if (this.mode === 'sectionText') {
              this.updateSection();
            } else {
              this.updateInline();
            }
          } catch (error) {
            console.error('scroll-reveal-text: Error in scroll handler', error);
          }
        });
      }

      /**
       * Throttled resize event handler using requestAnimationFrame
       * Marks sequence data as stale to trigger recalculation on next update
       * Recalculates metrics and sizing for layout changes
       */
      onResize() {
        if (!this.isConnected || this.allChars.length === 0) return;
        if (this.rafId) return;

        this.rafId = requestAnimationFrame(() => {
          this.rafId = 0;

          if (!this.isConnected) return;

          try {
            if (this.mode === 'sectionText') {
              // Mark sequence data as stale on resize (layout may have changed)
              this.sequenceDataStale = true;
              // Reset peer check counter to verify peers after layout change
              this._checkPeerCountUntil = 10;
              // Recalculate duration multiplier based on new text height/viewport ratio
              this.calculateDurationMultiplier();
              this.applyPinnedSizing();
              this.updateMetrics();
              this.updateSection();
            } else {
              this.updateInline();
            }
          } catch (error) {
            console.error('scroll-reveal-text: Error in resize handler', error);
          }
        });
      }

      /**
       * Safari fix: Handle visibility changes (e.g., when navigating back to page)
       * Safari's bfcache (back-forward cache) can cause state issues when returning to a page
       */
      onVisibilityChange() {
        if (!this.isConnected || this.allChars.length === 0) return;

        // Only proceed if page is now visible
        if (document.hidden) return;

        // Clear any pending animation frame
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
          this.rafId = 0;
        }

        // Invalidate cached rect to force fresh measurements
        this.cachedRect = null;
        this.cachedRectTimestamp = 0;

        // Schedule update using double RAF for Safari
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!this.isConnected) return;
            try {
              if (this.mode === 'sectionText') {
                // Recalculate sizing and metrics after visibility change
                this.applyPinnedSizing();
                this.updateMetrics();
                this.updateSection();
              } else {
                this.updateInline();
              }
            } catch (error) {
              console.error('scroll-reveal-text: Error in visibility change handler', error);
            }
          });
        });
      }

      /**
       * Calculates scroll metrics for sectionText mode
       * - Determines scroll start position (absolute Y coordinate)
       * - Calculates scroll duration needed for full text reveal
       * - Accounts for sticky header height in pinned height calculations
       * - Includes section padding-top to align animation start with actual content position
       * - Animation ends when user scrolls to the very bottom of the section
       */
      updateMetrics() {
        if (this.mode !== 'sectionText' || !this.section) return;

        try {
          const rect = this.section.getBoundingClientRect();

          // Get section padding to account for content offset
          const computedStyle = window.getComputedStyle(this.section);
          const paddingTop = parseFloat(computedStyle.paddingTop) || 0;

          // Start animation from where content actually begins (section top + padding)
          this.metrics.start = Math.round(rect.top + window.scrollY + paddingTop);

          const viewport = window.innerHeight || 0;
          const headerHeight = this.getHeaderHeight();

          // Animation should end when section bottom reaches viewport bottom
          // End position: (section top + section height) - viewport height
          const sectionBottom = rect.top + window.scrollY + rect.height;
          const animationEnd = Math.round(sectionBottom - viewport + headerHeight);

          // Duration is the scroll distance from start to end
          this.metrics.duration = Math.max(1, animationEnd - this.metrics.start);
        } catch (error) {
          console.error('scroll-reveal-text: Error updating metrics', error);
        }
      }

      /**
       * Updates character visibility for sectionText mode
       * Calculates progress based on current scroll position vs metrics
       * Passes raw (unclamped) progress to applyVisibility for proper timing control
       */
      updateSection() {
        if (!this.metrics) return;

        try {
          // For the first few scroll events, check if peer elements have been added
          // This handles race conditions where elements initialize at different times
          // After initialization period, we skip this check for performance
          let peersChanged = false;
          if (this._checkPeerCountUntil > 0) {
            this._checkPeerCountUntil--;

            let currentValidPeerCount = 1;
            if (this.section) {
              const allPeers = Array.from(this.section.querySelectorAll('scroll-reveal-text'));
              const validPeers = allPeers.filter((el) => el.allChars && el.allChars.length > 0);
              currentValidPeerCount = validPeers.length;
            }
            peersChanged = currentValidPeerCount !== this._lastPeerCount;
            if (peersChanged) {
              this._lastPeerCount = currentValidPeerCount;
            }
          }

          // Recalculate sequence data when:
          // 1. Stale flag is set (resize or initial setup)
          // 2. Number of valid peer elements has changed (during initialization period)
          if (this.sequenceDataStale || peersChanged) {
            this.setSequenceData();
            this.sequenceDataStale = false;
          }

          const scroll = window.scrollY || window.pageYOffset || 0;
          const progress = (scroll - this.metrics.start) / this.metrics.duration;
          this.applyVisibility(progress);
        } catch (error) {
          console.error('scroll-reveal-text: Error updating section', error);
        }
      }

      /**
       * Updates character visibility for inline/sticky-image-cards mode
       * - Caches getBoundingClientRect for 100ms to reduce reflows
       * - Uses viewport middle as anchor point for sticky-image-cards sections
       * - Falls back to offset-based calculation for standard inline elements
       */
      updateInline() {
        const now = performance.now();
        let rect;

        // Cache getBoundingClientRect for a short duration
        if (this.cachedRect && now - this.cachedRectTimestamp < this.RECT_CACHE_DURATION) {
          rect = this.cachedRect;
        } else {
          rect = this.getBoundingClientRect();
          this.cachedRect = rect;
          this.cachedRectTimestamp = now;
        }

        const vh = window.innerHeight || 0;
        const headerHeight = this.getHeaderHeight();
        const viewportMiddle = vh / 2;

        // Check if inside sticky-image-cards section
        try {
          const stickySection = this.closest('[data-section-type="sticky-image-cards"]');

          if (stickySection) {
            const textHeight = rect.height;

            // Skip if element is hidden (inside display:none container)
            if (textHeight === 0) return;

            const isMobile = this.isMobile();

            if (isMobile) {
              // Mobile: Animation timing
              // Start: when text top reaches 85% down the viewport (15% from bottom)
              // End: when sticky card wrapper bottom reaches viewport bottom

              // Find the sticky card wrapper that contains this text
              const cardWrapper = this.closest('[data-sticky-card-wrapper]');
              let endTop;
              let wrapperBottom = null;

              if (cardWrapper) {
                const wrapperRect = cardWrapper.getBoundingClientRect();
                wrapperBottom = Math.round(wrapperRect.bottom);
                // Calculate where text top will be when wrapper bottom reaches vh (viewport bottom)
                // Distance wrapper bottom needs to travel: wrapperRect.bottom - vh
                // Text top will be at: rect.top - (wrapperRect.bottom - vh)
                endTop = rect.top - wrapperRect.bottom + vh;
              } else {
                // Fallback: end when text bottom reaches viewport bottom
                endTop = vh - textHeight;
              }

              // Start point: when text top is at 85% down viewport (15% from bottom)
              const startTop = vh * 0.85;

              const scrollDistance = Math.max(1, startTop - endTop);
              const scrolled = startTop - rect.top;

              // progress: 0 at startTop, 1 at endTop
              const progress = this.clamp01(scrolled / scrollDistance);

              this.applyVisibility(progress);
              return;
            }

            // Desktop: Delay animation start so text isn't partially animated when entering viewport
            // Start: when text top reaches 80% down viewport (was: bottom or middle)
            // End: when text bottom reaches viewport middle
            const startTop = vh * 0.8;
            const endTop = viewportMiddle - textHeight;

            // Calculate scroll distance needed for animation
            const scrollDistance = Math.max(1, startTop - endTop);

            // Current position: distance from animation start
            const scrolled = startTop - rect.top;

            // Calculate progress: 0 at start, 1 when bottom reaches middle
            const progress = this.clamp01(scrolled / scrollDistance);

            this.applyVisibility(progress);
            return;
          }
        } catch (error) {
          console.warn('scroll-reveal-text: Error in sticky cards calculation', error);
        }

        // Fallback for non-sticky-cards elements (original behaviour)
        const startOffset = vh * this.START_OFFSET_RATIO;
        const endOffset = vh * this.END_OFFSET_RATIO;
        const denom = Math.max(1, Math.round((startOffset + rect.height - endOffset) * this.DURATION_MULTIPLIER));
        const progress = this.clamp01((startOffset - rect.top) / denom);
        this.applyVisibility(progress);
      }

      /**
       * Applies opacity values to character spans based on scroll progress (0-1)
       * - Characters before visible index: opacity 1 (fully visible)
       * - Characters in tail range: graduated opacity (0.82, 0.64, 0.46, 0.28)
       * - Characters beyond tail: base opacity (0.1)
       * - Batches DOM updates for performance
       * - Updates highlight states after visibility changes
       */
      applyVisibility(progress) {
        const total = this.allChars.length;
        if (total === 0) return;

        // For sectionText mode: check original progress BEFORE clamping
        // This ensures animation only starts when element is actually sticky (progress >= 0)
        const originalProgress = progress;
        const safeProgress = this.clamp01(progress);
        const adjustedProgress = this.sequenceTotal > 1 ? this.getSequenceProgress(safeProgress) : safeProgress;

        // For sectionText mode: start revealing immediately when progress >= 0 (no delay)
        // Show at least 1 character as soon as element becomes sticky (originalProgress >= 0)
        let visible;
        if (this.mode === 'sectionText' && originalProgress >= 0) {
          // For sequenced elements: adjustedProgress is 0 when not in our segment yet (due to clamping)
          if (this.sequenceTotal > 1) {
            if (adjustedProgress === 0) {
              // Not in our segment yet - show no characters
              visible = 0;
            } else {
              // In our segment - show characters
              visible = Math.max(1, Math.ceil(adjustedProgress * total));
            }
          } else {
            // Single element (no sequencing)
            visible = adjustedProgress === 0 ? 1 : Math.max(1, Math.ceil(adjustedProgress * total));
          }
        } else {
          visible = Math.floor(adjustedProgress * total);
        }

        if (visible === this.state.lastVisible) return;
        this.state.lastVisible = visible;

        // Batch DOM updates for better performance
        const updates = [];

        if (visible <= 0) {
          // Set all to base opacity
          for (let i = 0; i < total; i++) {
            const char = this.allChars[i];
            if (char && char.style.opacity !== this.BASE_OPACITY) {
              updates.push({char, opacity: this.BASE_OPACITY});
            }
          }
        } else if (visible >= total) {
          // Set all to full opacity
          for (let i = 0; i < total; i++) {
            const char = this.allChars[i];
            if (char && char.style.opacity !== '1') {
              updates.push({char, opacity: '1'});
            }
          }
        } else {
          // Calculate opacity for each character
          for (let i = 0; i < total; i++) {
            let next = this.BASE_OPACITY;
            if (i < visible) {
              next = '1';
            } else {
              const leadIndex = i - visible;
              if (leadIndex >= 0 && leadIndex < this.TAIL_OPACITIES.length) {
                next = this.TAIL_OPACITIES[leadIndex];
              }
            }

            const char = this.allChars[i];
            if (char && char.style.opacity !== next) {
              updates.push({char, opacity: next});
            }
          }
        }

        // Apply all updates in a single batch
        if (updates.length > 0) {
          for (const {char, opacity} of updates) {
            char.style.opacity = opacity;
          }
        }

        this.updateHighlightStates();
      }

      /**
       * Splits text content into individual character spans for animation
       * - Walks DOM tree to find text nodes (skips SVG, script, style, noscript)
       * - Adds the flourish icon as the first character, so it's revealed together with the text after it
       * - Wraps each character in a span with data-srt-char attribute
       * - Preserves DOM structure (doesn't rebuild to avoid breaking custom elements)
       * - Returns array of character spans and a revert function to restore original HTML
       */
      splitToChars(element) {
        if (!element) {
          throw new Error('Element is required');
        }

        const original = element.innerHTML;
        const chars = [];

        // IMPORTANT:
        // Do NOT rebuild DOM by cloning element nodes (it can recreate custom elements like <text-highlight>
        // before children are attached, breaking their initialization). Instead, wrap text nodes in-place.
        const textNodes = [];
        const walker = document.createTreeWalker(
          element,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode: (node) => {
              try {
                // Skip empty text nodes
                if (!node || typeof node.nodeValue !== 'string' || node.nodeValue.length === 0) {
                  return NodeFilter.FILTER_REJECT;
                }

                // Skip whitespace-only text nodes (newlines, spaces, tabs)
                // These are typically formatting in the HTML source, not actual content
                if (node.nodeValue.trim().length === 0) {
                  return NodeFilter.FILTER_REJECT;
                }

                const parentEl = node.parentElement;
                if (!parentEl) return NodeFilter.FILTER_REJECT;

                // Skip inside SVGs (avoid corrupting SVG structure/whitespace handling)
                if (parentEl.namespaceURI === 'http://www.w3.org/2000/svg') {
                  return NodeFilter.FILTER_REJECT;
                }

                // Skip script/style/noscript
                const tag = parentEl.tagName;
                if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT') {
                  return NodeFilter.FILTER_REJECT;
                }

                return NodeFilter.FILTER_ACCEPT;
              } catch (error) {
                return NodeFilter.FILTER_REJECT;
              }
            },
          },
          false
        );

        let current;
        while ((current = walker.nextNode())) {
          textNodes.push(current);
        }

        for (const node of textNodes) {
          const text = node.nodeValue || '';
          const frag = document.createDocumentFragment();

          for (let i = 0; i < text.length; i++) {
            const span = document.createElement('span');
            span.textContent = text[i];
            span.style.opacity = this.BASE_OPACITY;
            span.setAttribute('data-srt-char', 'true');
            chars.push(span);
            frag.appendChild(span);
          }

          node.parentNode?.replaceChild(frag, node);
        }

        // Each flourish is added before the first character that follows it, so it's revealed together with its text
        element.querySelectorAll('.flourish').forEach((flourish) => {
          const index = chars.findIndex((char) => flourish.compareDocumentPosition(char) & Node.DOCUMENT_POSITION_FOLLOWING);
          flourish.style.opacity = this.BASE_OPACITY;
          flourish.setAttribute('data-srt-char', 'true');
          chars.splice(index === -1 ? chars.length : index, 0, flourish);
        });

        const revert = () => {
          element.innerHTML = original;
        };

        return {chars, revert};
      }

      /**
       * Updates data-is-active attribute on text-highlight custom elements
       * - For most highlight types: activates when last character is nearly visible (opacity >= 0.64)
       * - For specific types or low-contrast scenarios: activates when first character becomes visible
       * - Automatically deactivates when scrolling up and characters become less visible (reverses animation)
       */
      updateHighlightStates() {
        try {
          const highlights = this.querySelectorAll('text-highlight');
          highlights.forEach((highlight) => {
            if (!highlight || !highlight.isConnected) return;

            const chars = highlight.querySelectorAll('span[data-srt-char="true"]');
            if (!chars || chars.length === 0) return;

            const highlightType = highlight.getAttribute('data-highlight-type');
            const shouldTriggerEarly = this.shouldTriggerHighlightEarly(highlight, highlightType);
            const currentState = highlight.getAttribute('data-is-active');

            if (shouldTriggerEarly) {
              // Activate when the first character becomes visible (for better visual effect)
              // Deactivate when scrolling up and first character fades below threshold
              let firstOpacity = 0;
              for (const ch of chars) {
                firstOpacity = parseFloat(ch.style.opacity || '0');
                break;
              }

              // Use threshold of 0.1 to trigger both activation and deactivation
              // This ensures smooth bidirectional behavior when scrolling up/down
              const next = firstOpacity > 0.1 ? 'true' : 'false';
              if (currentState !== next) {
                highlight.setAttribute('data-is-active', next);
              }
            } else {
              // Default: activate when the last character is nearly fully revealed
              // Deactivate when scrolling up and last character fades below threshold
              const lastChar = chars[chars.length - 1];
              const lastOpacity = parseFloat(lastChar.style.opacity || '0');

              // Use threshold of 0.64 (in the tail fade range) for smooth transitions
              // When scrolling up, as soon as last char drops below 0.64, highlight resets
              const next = lastOpacity >= 0.64 ? 'true' : 'false';
              if (currentState !== next) {
                highlight.setAttribute('data-is-active', next);
              }
            }
          });
        } catch (error) {
          // No-op: highlight animation is optional
        }
      }

      /**
       * Determines if a highlight should trigger early (on first character) vs late (on last character)
       * Triggers early for:
       * - squiggle type (better visual flow)
       * - alternate-font type (text changes immediately)
       * - highlight type with low contrast between text color and section background
       */
      shouldTriggerHighlightEarly(highlight, highlightType) {
        try {
          // Always trigger early for squiggle and alternate-font types
          if (highlightType === 'squiggle' || highlightType === 'alternate-font') {
            return true;
          }

          // For highlight type, check color contrast with section background
          if (highlightType === 'highlight') {
            const colorSection = highlight.closest('[class*="color-"]');
            if (!colorSection) return false;

            const sectionBgColor = this.getComputedCSSVariable(colorSection, '--bg');
            const highlightTextColor = this.getComputedCSSVariable(highlight, '--highlight-text-color', '--heading');

            if (sectionBgColor && highlightTextColor) {
              if (sectionBgColor === highlightTextColor) return true;

              const contrast = this.getColorContrast(sectionBgColor, highlightTextColor);
              // If contrast ratio is less than 2.5, colors are too similar - trigger early
              // This ensures the highlight is visible throughout the reveal animation
              return contrast < 2.5;
            }
          }

          return false;
        } catch (error) {
          return false;
        }
      }

      /**
       * Gets a computed CSS variable value from an element
       * Supports fallback variables (e.g., var(--primary, --secondary))
       */
      getComputedCSSVariable(element, varName, fallbackVarName = null) {
        try {
          const styles = window.getComputedStyle(element);
          let value = styles.getPropertyValue(varName).trim();

          // If empty and fallback exists, try fallback
          if ((!value || value === '') && fallbackVarName) {
            value = styles.getPropertyValue(fallbackVarName).trim();
          }

          return value || null;
        } catch (error) {
          return null;
        }
      }

      /**
       * Calculates the contrast ratio between two colors
       * Uses WCAG formula: https://www.w3.org/TR/WCAG20/#contrast-ratiodef
       * Returns a number between 1 (no contrast) and 21 (maximum contrast)
       */
      getColorContrast(color1, color2) {
        try {
          const lum1 = this.getRelativeLuminance(color1);
          const lum2 = this.getRelativeLuminance(color2);

          const lighter = Math.max(lum1, lum2);
          const darker = Math.min(lum1, lum2);

          return (lighter + 0.05) / (darker + 0.05);
        } catch (error) {
          return 21; // Return max contrast on error (assume colors are different)
        }
      }

      /**
       * Calculates relative luminance of a color
       * Supports rgb(), rgba(), hex, and named colors
       */
      getRelativeLuminance(color) {
        try {
          const rgb = this.parseColor(color);
          if (!rgb) return 0;

          // Convert to sRGB
          const rsRGB = rgb.r / 255;
          const gsRGB = rgb.g / 255;
          const bsRGB = rgb.b / 255;

          // Apply gamma correction
          const r = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
          const g = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
          const b = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

          // Calculate relative luminance
          return 0.2126 * r + 0.7152 * g + 0.0722 * b;
        } catch (error) {
          return 0;
        }
      }

      /**
       * Parses a CSS color string to RGB values
       * Supports: rgb(a), hex (#xxx, #xxxxxx), and uses canvas for named colors
       */
      parseColor(color) {
        try {
          if (!color) return null;

          // Handle rgb/rgba format
          const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (rgbMatch) {
            return {
              r: parseInt(rgbMatch[1]),
              g: parseInt(rgbMatch[2]),
              b: parseInt(rgbMatch[3]),
            };
          }

          // Handle hex format
          const hexMatch = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
          if (hexMatch) {
            return {
              r: parseInt(hexMatch[1], 16),
              g: parseInt(hexMatch[2], 16),
              b: parseInt(hexMatch[3], 16),
            };
          }

          // Handle short hex format (#abc -> #aabbcc)
          const shortHexMatch = color.match(/^#?([a-f\d])([a-f\d])([a-f\d])$/i);
          if (shortHexMatch) {
            return {
              r: parseInt(shortHexMatch[1] + shortHexMatch[1], 16),
              g: parseInt(shortHexMatch[2] + shortHexMatch[2], 16),
              b: parseInt(shortHexMatch[3] + shortHexMatch[3], 16),
            };
          }

          // For named colors or other formats, use canvas to parse
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = 1;
          const ctx = canvas.getContext('2d');
          if (!ctx) return null;

          ctx.fillStyle = color;
          ctx.fillRect(0, 0, 1, 1);
          const imageData = ctx.getImageData(0, 0, 1, 1).data;

          return {
            r: imageData[0],
            g: imageData[1],
            b: imageData[2],
          };
        } catch (error) {
          return null;
        }
      }

      /**
       * Calculates dynamic duration multiplier based on text element height
       * Shorter text (like single-line headings) gets faster animation
       * Taller text (like multi-paragraph body) gets slower animation
       * Only used in sectionText mode
       */
      calculateDurationMultiplier() {
        if (this.mode !== 'sectionText' || !this.contentRoot) return;

        try {
          const textHeight = this.contentRoot.scrollHeight || this.contentRoot.offsetHeight || 0;
          const viewport = window.innerHeight || 0;

          if (textHeight === 0 || viewport === 0) {
            // Fallback to default
            this.DURATION_MULTIPLIER = 2.5;
            return;
          }

          // Calculate text height as percentage of viewport
          const heightRatio = textHeight / viewport;

          // Dynamic multiplier based on height ratio:
          // - Small text (< 10% of viewport, ~1-2 lines): 1.2-1.5x
          // - Medium text (10-30% of viewport, ~3-8 lines): 1.5-2.0x
          // - Large text (30-50% of viewport, ~10+ lines): 2.0-2.5x
          // - Very large text (> 50% of viewport): 2.5-3.0x
          if (heightRatio < 0.1) {
            // Very short text (1-2 lines) - fast animation
            this.DURATION_MULTIPLIER = 1.2;
          } else if (heightRatio < 0.2) {
            // Short text (3-5 lines) - moderately fast
            this.DURATION_MULTIPLIER = 1.8;
          } else if (heightRatio < 0.3) {
            // Medium text (6-8 lines) - moderate speed
            this.DURATION_MULTIPLIER = 2;
          } else if (heightRatio < 0.5) {
            // Tall text (10+ lines) - slower
            this.DURATION_MULTIPLIER = 2.3;
          } else {
            // Very tall text (many paragraphs) - slowest
            this.DURATION_MULTIPLIER = 2.5;
          }
        } catch (error) {
          console.warn('scroll-reveal-text: Error calculating duration multiplier', error);
          this.DURATION_MULTIPLIER = 2.5; // Fallback to default
        }
      }

      /**
       * Detects if viewport is mobile size
       * Uses theme.isMobile() if available, otherwise falls back to width check
       */
      isMobile() {
        try {
          if (window.theme && typeof window.theme.isMobile === 'function') {
            return window.theme.isMobile();
          }
        } catch (error) {
          // Fall through to fallback
        }
        return window.innerWidth < this.MOBILE_BREAKPOINT;
      }

      /**
       * Gets sticky header height from theme for viewport calculations
       * Returns 0 if theme method is unavailable
       */
      getHeaderHeight() {
        try {
          const h = window.theme?.readHeights?.().stickyHeaderHeight;
          return Number(h) || 0;
        } catch (error) {
          return 0;
        }
      }

      /**
       * Determines this element's position in a sequence of multiple scroll-reveal-text elements
       * Used to stagger reveals when multiple elements exist in the same section
       * Only counts elements that have characters to animate (skips empty elements)
       */
      setSequenceData() {
        if (this.mode !== 'sectionText' || !this.section) {
          this.sequenceIndex = 0;
          this.sequenceTotal = 1;
          return;
        }

        const allPeers = Array.from(this.section.querySelectorAll('scroll-reveal-text'));
        // Only count elements that have characters to animate
        const validPeers = allPeers.filter((el) => el.allChars && el.allChars.length > 0);
        const idx = validPeers.indexOf(this);

        this.sequenceTotal = validPeers.length || 1;
        this.sequenceIndex = idx >= 0 ? idx : 0;
      }

      /**
       * Converts global scroll progress to local progress for this element in a sequence
       * Divides the total progress range into equal segments for each element
       */
      getSequenceProgress(globalProgress) {
        const segments = Math.max(1, this.sequenceTotal);
        const segmentSize = 1 / segments;
        const start = this.sequenceIndex * segmentSize;
        const local = (globalProgress - start) / segmentSize;
        return this.clamp01(local);
      }

      /**
       * Utility to clamp a value between 0 and 1
       */
      clamp01(v) {
        return v < 0 ? 0 : v > 1 ? 1 : v;
      }

      /**
       * Cleanup method:
       * - Removes event listeners
       * - Cancels pending animation frames
       * - Reverts DOM to original HTML (removes character spans)
       * - Cleans up inline styles and references
       */
      disconnectedCallback() {
        // Remove event listeners
        if (this._listening) {
          document.removeEventListener('theme:scroll', this.onScrollCallback);
          document.removeEventListener('theme:resize', this.onResizeCallback);
          document.removeEventListener('visibilitychange', this.onVisibilityChangeCallback);
          this._listening = false;
        }

        // Cancel pending animation frame
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
          this.rafId = 0;
        }

        // Revert DOM changes
        try {
          if (this.reverts && Array.isArray(this.reverts)) {
            this.reverts.forEach((fn) => {
              if (typeof fn === 'function') {
                fn();
              }
            });
          }
        } catch (error) {
          console.warn('scroll-reveal-text: Error reverting changes', error);
        }

        // Clean up styles
        if (this.wrapper) {
          this.wrapper.style.minHeight = '';
        }
        if (this.textEl) {
          this.textEl.style.height = '';
          this.textEl.style.minHeight = '';
        }

        // Clear references
        this.allChars = [];
        this.reverts = [];
        this.cachedRect = null;
      }

      /**
       * Sets up pinned/sticky scroll dimensions for sectionText mode
       * - Calculates minimum wrapper height (pinned height + scroll distance)
       * - Sets text element to fill viewport minus header height
       * - Creates the "sticky" scroll effect as user scrolls through section
       * - Accounts for section padding to align animation with content position
       */
      applyPinnedSizing() {
        if (this.mode !== 'sectionText' || !this.wrapper || !this.textEl) return;

        try {
          const viewport = window.innerHeight || 0;
          const headerHeight = this.getHeaderHeight();
          const contentHeight = this.textEl ? this.textEl.scrollHeight : this.contentRoot?.scrollHeight || 0;
          const minPinned = Math.max(0, viewport - headerHeight);

          // Get section padding to adjust wrapper height accordingly
          const computedStyle = window.getComputedStyle(this.section);
          const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
          const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
          const totalPadding = paddingTop + paddingBottom;

          // Subtract section padding from wrapper height so scroll distance aligns with content
          const minWrapper = minPinned + Math.round(contentHeight * this.DURATION_MULTIPLIER) - totalPadding;

          this.wrapper.style.minHeight = `${Math.round(minWrapper)}px`;
          this.textEl.style.height = 'auto';
          this.textEl.style.minHeight = `${Math.round(minPinned)}px`;
        } catch (error) {
          console.error('scroll-reveal-text: Error applying pinned sizing', error);
        }
      }
    }
  );
}
